import React from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

export interface DynamicJsonFormProps {
  schema: any;
  value: Record<string, any>;
  onChange: (value: Record<string, any>) => void;
  disabled?: boolean;
}

/**
 * A lightweight Dynamic Form component that renders MUI fields based on a JSON Schema.
 * Supports basic types: string, number, integer, boolean.
 * Supports "password" format for strings.
 */
const DynamicJsonForm: React.FC<DynamicJsonFormProps> = ({
  schema,
  value,
  onChange,
  disabled = false,
}) => {
  if (!schema || schema.type !== 'object' || !schema.properties) {
    return null;
  }

  const properties = schema.properties;
  const required = schema.required || [];

  const handleFieldChange = (key: string, fieldValue: any) => {
    onChange({
      ...value,
      [key]: fieldValue,
    });
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {Object.entries(properties).map(([key, prop]: [string, any]) => {
          const isRequired = required.includes(key);
          const fieldLabel = prop.title || key;
          const fieldDescription = prop.description;
          const fieldValue = value[key] ?? prop.default ?? '';

          // Render based on type
          if (prop.type === 'boolean') {
            return (
              <Grid size={{ xs: 12 }} key={key}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(fieldValue)}
                      onChange={(e) => handleFieldChange(key, e.target.checked)}
                      disabled={disabled}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">{fieldLabel}</Typography>
                      {fieldDescription && (
                        <Typography variant="caption" color="text.secondary">
                          {fieldDescription}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </Grid>
            );
          }

          if (prop.type === 'integer' || prop.type === 'number') {
            return (
              <Grid size={{ xs: 12 }} key={key}>
                <TextField
                  fullWidth
                  label={fieldLabel}
                  type="number"
                  value={fieldValue}
                  onChange={(e) => handleFieldChange(key, e.target.value === '' ? undefined : Number(e.target.value))}
                  required={isRequired}
                  helperText={fieldDescription}
                  disabled={disabled}
                />
              </Grid>
            );
          }

          // Default to string/textfield
          const isPassword = prop.format === 'password' || key.toLowerCase().includes('secret') || key.toLowerCase().includes('password');
          
          return (
            <Grid size={{ xs: 12 }} key={key}>
              <TextField
                fullWidth
                label={fieldLabel}
                type={isPassword ? 'password' : 'text'}
                value={fieldValue}
                onChange={(e) => handleFieldChange(key, e.target.value)}
                required={isRequired}
                helperText={fieldDescription}
                disabled={disabled}
                multiline={prop.maxLength > 200 || prop.type === 'textarea'}
                rows={prop.maxLength > 200 ? 3 : 1}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default DynamicJsonForm;
